/* eslint-disable func-names */
/* eslint-disable no-console */
// @flow

import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  MobileStepper,
  Paper,
  Snackbar,
  Typography,
} from '@mui/material';
import * as React from 'react';
// $FlowFixMe
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';

// $FlowFixMe
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// $FlowFixMe
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import { useForm, useFieldArray } from 'react-hook-form';

import useLocation from 'Hooks/useLocation';

// $FlowFixMe
import * as yup from 'yup';

import { convertFileToBase64 } from 'Utils/image';

import moment from 'moment';

// $FlowFixMe
import { yupResolver } from '@hookform/resolvers/yup';

import radioWithOtherYup from 'Utils/radioWithOtherValidation';

import Step1 from './step1';
import Step2 from './step2';
import Step3 from './step3';
import Step4 from './step4';

const schema = yup
  .object({
    siteId: yup.string().required(),
    siteName: yup.string().required(),
    siteLocation: yup.string().required(),
    reportStartDate: yup.object().required(),
    fiberProvider: radioWithOtherYup('Please enter the client name'),
    numberOfUnits: yup.string().required(),
    cablingType: radioWithOtherYup('Please enter the cabling type'),
    leadEngineerForBuild: yup.string().required(),
    endDateOfStageOneCablingWork: yup.object().required(),
    items: yup
      .array()
      .nullable()
      .of(
        yup.object().shape({
          enclosureID: yup.string().required(),
          enclosure: yup.string().required(),
          closeUpImageOfSplitterWallBox: yup.mixed().nullable().required(),
          wideViewImageOfSplitterWallbox: yup.mixed().nullable().required(),
          cablingIsInstalled: yup.string().required(),
          closeUpImageOfCableRoutes: yup.mixed().nullable().required(),
          wideViewImageOfCableRoutes: yup.mixed().nullable().required(),
          cablingEntering: yup.string().required(),
          newOrExistingContainment: yup.string().required(),
          poeCspInstalled: yup.string().required(),
          imageOfPoeCsp: yup.mixed().nullable().required(),
        }),
      )
      .min(1),
    generalNotesAndComments: yup.string().nullable(),
    operativeSignature: yup.string().nullable().required(),
  })
  .required();

type Props = {|
  onSubmit: Function,
  onProcessingStart?: () => void,
  onProcessingEnd?: () => void,
  isProcessing?: boolean,
  processingMessage?: string,
|};

const steps = [
  { label: 'Title Page' },
  { label: 'Build Summary' },
  { label: 'As Built Inventory Log' },
  { label: 'Signatories' },
];

const defaultValues = {
  siteId: '',
  siteName: '',
  siteLocation: '',
  reportStartDate: moment(),

  fiberProvider: '',
  numberOfUnits: '',
  cablingType: '',
  leadEngineerForBuild: '',
  endDateOfStageOneCablingWork: moment(),

  items: [],
  generalNotesAndComments: null,
  signatureImage: null,
};

const IMAGE_FIELDS = [
  'closeUpImageOfSplitterWallBox',
  'wideViewImageOfSplitterWallbox',
  'closeUpImageOfCableRoutes',
  'wideViewImageOfCableRoutes',
  'imageOfPoeCsp',
];

const InstallationAsBuildForm = (props: Props): React.Node => {
  const {
    onSubmit,
    onProcessingStart,
    onProcessingEnd,
    isProcessing = false,
    processingMessage = 'Preparing report...',
  } = props;

  const submitRef = React.useRef();
  const location = useLocation();

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const [activeStep, setActiveStep] = React.useState(0);
  const [hasError, setHasError] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const maxSteps = steps.length;

  React.useEffect(() => {
    if (location) {
      setValue('siteLocation', location);
    }
  }, [location, setValue]);

  const handleNext = async () => {
    if (activeStep === 3) {
      submitRef.current.click();

      return;
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleCloseError = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setHasError(false);
  };

  const handleOnSubmit = async (data) => {
    setIsSubmitting(true);
    onProcessingStart?.();

    try {
      const allImages = [];

      // eslint-disable-next-line no-restricted-syntax
      for (const item of data.items) {
        let helper = [];

        // eslint-disable-next-line no-restricted-syntax
        for (const key of IMAGE_FIELDS) {
          // eslint-disable-next-line no-restricted-syntax
          for (const image of item[key]) {
            let imageData = { fileName: null, base64: null };

            // eslint-disable-next-line no-await-in-loop
            imageData = await convertFileToBase64(image, true);
            helper.push(imageData);
            allImages.push(imageData);
          }
          item[key] = helper;
          helper = [];
        }
      }

      onSubmit({
        ...data,
        allImages,
        signature: [{ fileName: 'signature', base64: data.operativeSignature }],
      });
    } catch (err) {
      setIsSubmitting(false);
      onProcessingEnd?.();
      setHasError(true);
    }
  };

  const handleOnError = () => setHasError(true);

  const showProcessingOverlay = isSubmitting || isProcessing;

  return (
    <form onSubmit={handleSubmit(handleOnSubmit, handleOnError)}>
      <Backdrop
        open={showProcessingOverlay}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 2,
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" size={48} />

        <Typography variant="body1" color="inherit">
          {processingMessage}
        </Typography>
      </Backdrop>

      <Snackbar
        open={hasError}
        autoHideDuration={6000}
        onClose={handleCloseError}
      >
        <Alert severity="error">
          There is a problem. Please, check all required fields.
        </Alert>
      </Snackbar>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          height: 'calc(100vh - 64px)',
          maxHeight: 'calc(100vh - 64px)',
          minHeight: 0,
        }}
      >
        <Paper
          square
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            height: 50,
            bgcolor: 'background.default',
            paddingTop: '5px',
            position: 'fixed',
            top: 56,
            zIndex: 1,
            left: { xs: 0, sm: 240 },
            right: 0,
          }}
        >
          <Typography>{steps[activeStep].label}</Typography>

          <Typography sx={{ fontSize: '10px' }}>
            {`Page ${activeStep + 1} / ${maxSteps}`}
          </Typography>
        </Paper>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            display: 'flex',
            flexDirection: 'column',
            margin: '60px 10px 50px 10px',
          }}
        >
          <LocalizationProvider dateAdapter={AdapterMoment}>
            {activeStep === 0 && (
              <Step1 control={control} errors={errors} setValue={setValue} />
            )}

            {activeStep === 1 && (
              <Step2 control={control} errors={errors} setValue={setValue} />
            )}

            {activeStep === 2 && (
              <Step3
                control={control}
                errors={errors}
                fields={fields}
                append={append}
                remove={remove}
              />
            )}

            {activeStep === 3 && (
              <Step4
                control={control}
                errors={errors}
                getValues={getValues}
                setValue={setValue}
              />
            )}

            <Button type="submit" sx={{ display: 'none' }} ref={submitRef} />
          </LocalizationProvider>
        </Box>

        <MobileStepper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: { xs: 0, sm: 240 },
            right: 0,
          }}
          variant="text"
          steps={maxSteps}
          position="static"
          activeStep={activeStep}
          nextButton={
            <Button
              size="small"
              onClick={handleNext}
              disabled={showProcessingOverlay}
            >
              {activeStep === maxSteps - 1 ? 'Finish' : 'Next'}

              {activeStep < maxSteps - 1 && <KeyboardArrowRight />}
            </Button>
          }
          backButton={
            <Button
              size="small"
              onClick={handleBack}
              disabled={activeStep === 0 || showProcessingOverlay}
            >
              <KeyboardArrowLeft />
              Back
            </Button>
          }
        />
      </Box>
    </form>
  );
};

InstallationAsBuildForm.defaultProps = {
  onProcessingStart: undefined,
  onProcessingEnd: undefined,
  isProcessing: false,
  processingMessage: 'Preparing report...',
};

export default InstallationAsBuildForm;
